import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  Box,
  IconButton,
  Typography,
  Button,
  styled,
  alpha,
  Grow,
  List,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tabs,
  Tab,
  useMediaQuery,
  useTheme,
  Modal,
  Grid,
  Divider
} from '@mui/material';
import {
  Monitor,
  Sun,
  Moon,
  X,
  Check,
  Home,
  MessageCircle,
  Globe,
  Palette,
  Layout,
  Languages,
  ChevronDown,
  DollarSign,
  HeartPulse,
  Dumbbell,
  Plus,
  Wallet
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { ReactComponent as BrainIcon } from '../../../assets/images/loading-white.svg';
import {
  saveUserSelectedApps,
  setUserWallpaperPreference
} from '../services/AGTHallService';

// Gradientes específicos para cada AGT (desde AGTPromo)
// Light mode (originales)
const CHAT_GRADIENT_LIGHT = 'linear-gradient(to bottom, #415B7E, #2D476A, #193356)';
const UPS_GRADIENT_LIGHT = 'linear-gradient(to bottom, #DC9844, #C88430, #B4701C)';
const JOB_GRADIENT_LIGHT = 'linear-gradient(to bottom, #4A4A4A, #333333, #1A1A1A)';
const FIT_GRADIENT_LIGHT = 'linear-gradient(to bottom, #FF8C00, #FF6B00, #FF5722)';
const HEALTH_GRADIENT_LIGHT = 'linear-gradient(to bottom, #4CAF50, #3d8b40, #2e7031)';

// Dark mode (versiones más oscuras - reducidos ~40%)
const CHAT_GRADIENT_DARK = 'linear-gradient(to bottom, #2A3D54, #1E2F45, #0F1A2B)';
const UPS_GRADIENT_DARK = 'linear-gradient(to bottom, #8A5F2A, #7A501D, #6B4312)';
const JOB_GRADIENT_DARK = 'linear-gradient(to bottom, #2D2D2D, #1F1F1F, #0F0F0F)';
const FIT_GRADIENT_DARK = 'linear-gradient(to bottom, #994F00, #994000, #993314)';
const HEALTH_GRADIENT_DARK = 'linear-gradient(to bottom, #2D6930, #245326, #1B421D)';

// Color constants
const ACCENT_COLOR = '#0385FF';

// Apple color scheme
const appleColors = {
  light: {
    label: { primary: '#000000' },
    system: {
      grey: '#8E8E93',
      grey2: '#AEAEB2',
      grey3: '#C7C7CC',
      grey6: '#F2F2F7',
      inputBg: '#F2F2F7',
      green: '#31C859',
      white: '#FFFFFF',
      black: '#000000'
    },
    bg: {
      primaryElevated: '#FFFFFF',
      secondary: '#F2F2F7',
      tertiary: '#FAFAFA'
    },
    fill: { tertiary: '#767680' },
    ui: { accent: ACCENT_COLOR, base: '#F2F2F7' }
  },
  dark: {
    label: { primary: '#FFFFFF' },
    system: {
      grey: '#8E8E93',
      grey2: '#636366',
      grey3: '#464649',
      grey6: '#1C1C1E',
      inputBg: '#1C1C1E',
      green: '#2DD257',
      white: '#FFFFFF',
      black: '#000000'
    },
    bg: {
      primaryElevated: '#000000',
      secondary: '#1C1C1E',
      tertiary: '#2C2C2E'
    },
    fill: { tertiary: '#767680' },
    ui: { accent: ACCENT_COLOR, base: '#272729' }
  }
};

// Languages data
const languages = [
  { code: 'auto', name: 'Auto-detect', nativeName: 'Auto-detect' },
  { code: 'en', name: 'English', nativeName: 'English' },
  { code: 'es', name: 'Spanish', nativeName: 'Español' },
  { code: 'es-419', name: 'Spanish (Latin America)', nativeName: 'Español (Latinoamérica)' },
  { code: 'ca', name: 'Catalan', nativeName: 'Català' },
  { code: 'fr', name: 'French', nativeName: 'Français' },
  { code: 'fr-ca', name: 'French (Canada)', nativeName: 'Français (Canada)' },
  { code: 'de', name: 'German', nativeName: 'Deutsch' },
  { code: 'it', name: 'Italian', nativeName: 'Italiano' },
  { code: 'pt', name: 'Portuguese', nativeName: 'Português' },
  { code: 'pt-br', name: 'Portuguese (Brazil)', nativeName: 'Português (Brasil)' },
  { code: 'ru', name: 'Russian', nativeName: 'Русский' },
  { code: 'zh', name: 'Chinese (Simplified)', nativeName: '中文 (简体)' },
  { code: 'zh-tw', name: 'Chinese (Traditional)', nativeName: '中文 (繁體)' },
  { code: 'ja', name: 'Japanese', nativeName: '日本語' },
  { code: 'ko', name: 'Korean', nativeName: '한국어' },
  { code: 'ar', name: 'Arabic', nativeName: 'العربية' },
  { code: 'hi', name: 'Hindi', nativeName: 'हिन्दी' },
  { code: 'tr', name: 'Turkish', nativeName: 'Türkçe' },
  { code: 'nl', name: 'Dutch', nativeName: 'Nederlands' },
  { code: 'sv', name: 'Swedish', nativeName: 'Svenska' },
  { code: 'pl', name: 'Polish', nativeName: 'Polski' },
  { code: 'vi', name: 'Vietnamese', nativeName: 'Tiếng Việt' },
  { code: 'th', name: 'Thai', nativeName: 'ไทย' },
  { code: 'id', name: 'Indonesian', nativeName: 'Bahasa Indonesia' },
  { code: 'sw', name: 'Swahili', nativeName: 'Kiswahili' }
];

// Navigation menu items
const navItems = [
  { id: 'appearance', name: 'Appearance', icon: Palette },
  { id: 'startupTab', name: 'Ecosystem', icon: Layout },
  { id: 'language', name: 'Language', icon: Languages }
];

// Appearance options - Theme Mode
const appearanceOptions = [
  { name: 'System', type: 'system', icon: Monitor },
  { name: 'Light', type: 'light', icon: Sun },
  { name: 'Dark', type: 'dark', icon: Moon }
];

// Wallpaper options
const wallpaperOptions = [
  {
    name: 'No Wallpaper',
    type: 'none',
    icon: X,
    description: 'Default solid colors',
    preview: null
  },
  {
    name: 'Apartment',
    type: 'appartment',
    icon: Home,
    description: 'Day/Night apartment',
    preview: '/images/wallpaper/wallpaperday-appartment.png'
  },
  {
    name: 'Office',
    type: 'office',
    icon: Globe,
    description: 'Day office environment',
    preview: '/images/wallpaper/wallpaperday-office.png'
  },
  {
    name: 'Starbucks',
    type: 'starbucks',
    icon: Monitor,
    description: 'Coffee shop ambiance',
    preview: '/images/wallpaper/wallpaperday-starbucks.png'
  }
];

// Definición de todas las aplicaciones disponibles
const availableApps = [
  {
    id: 'home',
    name: 'Home',
    description: 'Central hub',
    icon: Home,
    required: true
  },
  {
    id: 'chat',
    name: 'Chat',
    description: 'AI conversation',
    gradient: CHAT_GRADIENT_LIGHT,
    gradientDark: CHAT_GRADIENT_DARK,
    required: true
  },
  {
    id: 'fin',
    name: 'Fin',
    description: 'Finance tracker',
    gradient: JOB_GRADIENT_LIGHT,
    gradientDark: JOB_GRADIENT_DARK,
    required: false
  },
  {
    id: 'fit',
    name: 'Fit',
    description: 'Fitness',
    gradient: FIT_GRADIENT_LIGHT,
    gradientDark: FIT_GRADIENT_DARK,
    required: false
  },
  {
    id: 'health',
    name: 'Health',
    description: 'Health tracking',
    gradient: HEALTH_GRADIENT_LIGHT,
    gradientDark: HEALTH_GRADIENT_DARK,
    required: false
  },
  {
    id: 'ups',
    name: 'Ups',
    description: 'Business tools',
    gradient: UPS_GRADIENT_LIGHT,
    gradientDark: UPS_GRADIENT_DARK,
    required: false
  },
];

// Function to get startup tab options based on selected apps
const getStartupTabOptions = (selectedApps = ['home', 'chat']) => {
  const baseOptions = [
    { name: 'Default', type: 'default', icon: Monitor, description: 'Use system behavior' }
  ];

  // Iconos para el dropdown (simples, no gradientes)
  const iconMap = {
    home: Home,
    chat: MessageCircle,
    ups: DollarSign,
    fin: Wallet,
    health: HeartPulse,
    fit: Dumbbell
  };

  const appOptions = selectedApps.map(appId => {
    const app = availableApps.find(a => a.id === appId);
    if (app) {
      return {
        name: app.name,
        type: appId,
        icon: app.icon || iconMap[appId] || Monitor,
        description: `Always start in ${app.name}`
      };
    }
    return null;
  }).filter(Boolean);

  return [...baseOptions, ...appOptions];
};

// Styled components mejorados
const BlurBackdrop = styled(Box)(({ theme, open }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: theme.palette.mode === 'dark'
    ? alpha('#000000', 0)
    : alpha('#000000', 0),
  backdropFilter: 'blur(2px)',
  zIndex: 1400,
  opacity: open ? 1 : 0,
  transition: 'opacity 300ms ease',
  visibility: open ? 'visible' : 'hidden',
}));

const AppleDialog = styled(Dialog)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    '& .MuiDialog-paper': {
      borderRadius: 20,
      backgroundColor: isWallpaperMode
        ? 'rgba(55, 55, 55, 0.1)'
        : (theme.palette.mode === 'dark'
          ? appleColors.dark.bg.primaryElevated
          : appleColors.light.bg.primaryElevated),
      backdropFilter: 'blur(10px)',
      WebkitBackdropFilter: 'blur(10px)',
      width: '800px',
      maxWidth: '95%',
      maxHeight: '85vh',
      margin: 16,
      boxShadow: theme.palette.mode === 'dark'
        ? '0px 20px 60px rgba(0, 0, 0, 0.6)'
        : '0px 20px 60px rgba(0, 0, 0, 0.2)',
      border: isWallpaperMode
        ? '1px solid rgba(55, 55, 55, 0.1)'
        : (theme.palette.mode === 'dark'
          ? '1px solid rgba(255, 255, 255, 0.1)'
          : 'none'),
      overflow: 'hidden',
      '@media (max-width: 600px)': {
        width: '100%',
        maxWidth: '100%',
        margin: '16px',
        maxHeight: '90vh',
      }
    },
    '& .MuiBackdrop-root': {
      backgroundColor: 'transparent',
    }
  };
});

// Tarjeta de aplicación mejorada con dimensiones consistentes
const AppCard = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'selected' && prop !== 'removable',
})(({ theme, selected, removable }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: theme.spacing(2),
  borderRadius: 16,
  border: selected
    ? theme.palette.actualMode === 'wallpaper'
      ? `2px solid #FFFFFF`
      : `2px solid ${ACCENT_COLOR}`
    : `2px solid ${theme.palette.mode === 'dark' ? '#333' : '#e0e0e0'}`,
  backgroundColor: selected
    ? theme.palette.actualMode === 'wallpaper'
      ? alpha('#FFFFFF', 0.1)
      : theme.palette.mode === 'dark'
        ? alpha(ACCENT_COLOR, 0.15)
        : alpha(ACCENT_COLOR, 0.05)
    : theme.palette.mode === 'dark'
      ? '#2C2C2E'
      : '#FFFFFF',
  cursor: 'pointer',
  transition: 'all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
  position: 'relative',
  opacity: removable === false ? 0.6 : 1,
  pointerEvents: removable === false ? 'none' : 'auto',
  minHeight: '120px',
  maxHeight: '120px',
  width: '100%',
  justifyContent: 'space-between',
  '&:hover': {
    transform: removable !== false ? 'translateY(-2px)' : 'none',
    boxShadow: removable !== false ? '0px 4px 12px rgba(0, 0, 0, 0.1)' : 'none',
  }
}));

// Componente para renderizar BrainIcon con gradiente
const BrainIconWithGradient = ({ gradient, gradientDark, appId }) => {
  const theme = useTheme();
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';
  const isDarkMode = theme.palette.mode === 'dark';
  const gradientId = `brain-gradient-settings-${appId}`;

  if (!gradient) {
    return <BrainIcon />;
  }

  const extractColors = (gradientString) => {
    const colorRegex = /#[0-9A-Fa-f]{6}/g;
    return gradientString.match(colorRegex) || [];
  };

  const invertColor = (hex) => {
    hex = hex.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    const maxValue = 180;
    const invR = Math.min(255 - r, maxValue).toString(16).padStart(2, '0');
    const invG = Math.min(255 - g, maxValue).toString(16).padStart(2, '0');
    const invB = Math.min(255 - b, maxValue).toString(16).padStart(2, '0');
    return `#${invR}${invG}${invB}`;
  };

  const activeGradient = (isDarkMode && gradientDark) ? gradientDark : gradient;
  const colors = extractColors(activeGradient);

  // En wallpaper mode
  if (isWallpaperMode) {
    if (appId === 'fit') {
      return (
        <>
          <svg width="0" height="0" style={{ position: 'absolute', pointerEvents: 'none' }}>
            <defs>
              <linearGradient id={gradientId} x1="0%" y1="0%" x2="0%" y2="100%">
                {colors.map((color, index) => (
                  <stop key={index} offset={`${(index / (colors.length - 1)) * 100}%`} stopColor={color} />
                ))}
              </linearGradient>
            </defs>
          </svg>
          <Box component="span" sx={{ display: 'contents', '& svg path': { fill: `url(#${gradientId})`, stroke: `url(#${gradientId})` } }}>
            <BrainIcon />
          </Box>
        </>
      );
    }

    if (appId === 'fin') {
      // Usar blanco puro para mejor visibilidad en wallpaper
      return <Box component="span" sx={{ display: 'contents', '& svg path': { fill: '#FFFFFF', stroke: '#FFFFFF' } }}><BrainIcon /></Box>;
    }

    const darkestColor = colors[colors.length - 1] || '#1A1A1A';
    return <Box component="span" sx={{ display: 'contents', '& svg path': { fill: darkestColor, stroke: darkestColor } }}><BrainIcon /></Box>;
  }

  // Chat en dark mode usa color sólido
  if (appId === 'chat' && isDarkMode) {
    const lightestColor = colors[0] || '#415B7E';
    return <Box component="span" sx={{ display: 'contents', '& svg path': { fill: lightestColor, stroke: lightestColor } }}><BrainIcon /></Box>;
  }

  // Fin usa colores invertidos
  const shouldInvertColors = appId === 'fin';
  const finalColors = shouldInvertColors ? colors.map(invertColor) : colors;

  return (
    <>
      <svg width="0" height="0" style={{ position: 'absolute', pointerEvents: 'none' }}>
        <defs>
          <linearGradient id={gradientId} x1="0%" y1="0%" x2="0%" y2="100%">
            {finalColors.map((color, index) => (
              <stop key={index} offset={`${(index / (finalColors.length - 1)) * 100}%`} stopColor={color} />
            ))}
          </linearGradient>
        </defs>
      </svg>
      <Box component="span" sx={{ display: 'contents', '& svg path': { fill: `url(#${gradientId})`, stroke: `url(#${gradientId})` } }}>
        <BrainIcon />
      </Box>
    </>
  );
};

const AppIcon = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'gradient' && prop !== 'gradientDark' && prop !== 'appId',
})(({ theme, gradient, gradientDark, appId }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';
  const isDarkMode = theme.palette.mode === 'dark';

  // Funciones auxiliares
  const extractColors = (gradientString) => {
    const colorRegex = /#[0-9A-Fa-f]{6}/g;
    return gradientString.match(colorRegex) || [];
  };

  const invertColor = (hex) => {
    hex = hex.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    const maxValue = 180;
    const invR = Math.min(255 - r, maxValue).toString(16).padStart(2, '0');
    const invG = Math.min(255 - g, maxValue).toString(16).padStart(2, '0');
    const invB = Math.min(255 - b, maxValue).toString(16).padStart(2, '0');
    return `#${invR}${invG}${invB}`;
  };

  const baseGradient = gradient
    ? (isDarkMode && gradientDark ? gradientDark : gradient)
    : (isDarkMode ? 'linear-gradient(to bottom, #1C1C1E, #0F0F11)' : 'linear-gradient(to bottom, #34aadc, #007aff)');

  // Chat en dark mode: borde sólido
  const isChatInDark = appId === 'chat' && isDarkMode && !isWallpaperMode;
  const chatLightColor = isChatInDark ? extractColors(gradientDark || gradient)[0] || '#415B7E' : null;

  // Fin en ambos modos: colores invertidos para el borde
  const isFin = appId === 'fin' && !isWallpaperMode;
  let borderGradient = baseGradient;

  if (isFin) {
    const gradColors = extractColors(gradientDark || gradient);
    const invertedColors = gradColors.map(invertColor);
    borderGradient = `linear-gradient(to bottom, ${invertedColors.join(', ')})`;
  }

  // Para Home: color del icono debe coincidir con el borde
  const homeIconColor = !gradient && !isDarkMode ? '#007aff' : '#FFFFFF';

  return {
    position: 'relative',
    width: 48,
    height: 48,
    borderRadius: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: theme.spacing(1),
    background: isWallpaperMode
      ? 'rgba(255, 255, 255, 0.15)'
      : (isDarkMode ? '#1C1C1E' : '#FFFFFF'),
    backdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    WebkitBackdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    border: isWallpaperMode ? '1px solid rgba(255, 255, 255, 0.2)' : 'none',
    color: homeIconColor,
    flexShrink: 0,
    '&::before': isWallpaperMode ? {} : {
      content: '""',
      position: 'absolute',
      inset: 0,
      borderRadius: 12,
      padding: '2px',
      background: isChatInDark ? chatLightColor : borderGradient,
      WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
      WebkitMaskComposite: 'xor',
      maskComposite: 'exclude',
      pointerEvents: 'none',
    },
    '& svg': {
      width: '70%',
      height: '70%',
    },
  };
});

const AppTextContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  minHeight: '42px',
  maxHeight: '42px',
  overflow: 'hidden',
  width: '100%',
}));

const AppName = styled(Typography)(({ theme }) => ({
  fontSize: '14px',
  fontWeight: 600,
  textAlign: 'center',
  color: theme.palette.actualMode === 'wallpaper'
    ? '#FFFFFF'
    : theme.palette.mode === 'dark' ? '#FFFFFF' : '#1d1d1f',
  marginBottom: theme.spacing(0.5),
  lineHeight: 1.2,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  width: '100%',
}));

const AppDescription = styled(Typography)(({ theme }) => ({
  fontSize: '11px',
  fontWeight: 400,
  textAlign: 'center',
  color: theme.palette.actualMode === 'wallpaper'
    ? alpha('#FFFFFF', 0.7)
    : theme.palette.mode === 'dark' ? '#8E8E93' : '#86868b',
  lineHeight: 1.2,
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  height: '22px',
  width: '100%',
}));

const RemoveButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: -10,
  right: -10,
  width: 26,
  height: 26,
  backgroundColor: '#FF3B30',
  color: '#FFFFFF',
  boxShadow: '0px 2px 8px rgba(255, 59, 48, 0.3)',
  '&:hover': {
    backgroundColor: '#D70015',
    transform: 'scale(1.1)',
    boxShadow: '0px 4px 12px rgba(255, 59, 48, 0.4)',
  },
  '& svg': {
    width: 16,
    height: 16,
  },
  zIndex: 10,
}));

const AddButton = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: -10,
  right: -10,
  width: 26,
  height: 26,
  borderRadius: '50%',
  backgroundColor: theme.palette.actualMode === 'wallpaper'
    ? '#FFFFFF'
    : ACCENT_COLOR,
  color: theme.palette.actualMode === 'wallpaper'
    ? '#000000'
    : '#FFFFFF',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  boxShadow: theme.palette.actualMode === 'wallpaper'
    ? '0px 2px 8px rgba(255, 255, 255, 0.3)'
    : '0px 2px 8px rgba(3, 133, 255, 0.3)',
  transition: 'all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
  '&:hover': {
    backgroundColor: theme.palette.actualMode === 'wallpaper'
      ? 'rgba(255, 255, 255, 0.85)'
      : '#0051d5',
    transform: 'scale(1.1)',
    boxShadow: theme.palette.actualMode === 'wallpaper'
      ? '0px 4px 12px rgba(255, 255, 255, 0.4)'
      : '0px 4px 12px rgba(3, 133, 255, 0.4)',
  },
  '& svg': {
    width: 16,
    height: 16,
  },
  zIndex: 10,
}));

const DialogHeader = styled(Box)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    borderBottom: `1px solid ${isWallpaperMode
      ? 'rgba(55, 55, 55, 0.1)'
      : (theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.1)'
        : 'rgba(0, 0, 0, 0.06)'
      )}`,
    padding: theme.spacing(2, 3),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  };
});

const ContentContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexGrow: 1,
  height: 'calc(100% - 130px)',
  overflow: 'hidden',
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    height: 'calc(100% - 150px)',
  }
}));

const SideNavigation = styled(Box)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    width: '200px',
    borderRight: `1px solid ${isWallpaperMode
      ? 'rgba(55, 55, 55, 0.1)'
      : (theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.1)'
        : 'rgba(0, 0, 0, 0.06)'
      )}`,
    overflow: 'hidden',
    padding: theme.spacing(2, 0),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    }
  };
});

const MobileNavigation = styled(Box)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    display: 'none',
    [theme.breakpoints.down('sm')]: {
      display: 'block',
      width: '100%',
      borderBottom: `1px solid ${isWallpaperMode
        ? 'rgba(55, 55, 55, 0.1)'
        : (theme.palette.mode === 'dark'
          ? 'rgba(255, 255, 255, 0.1)'
          : 'rgba(0, 0, 0, 0.06)'
        )}`,
    }
  };
});

const StyledTabs = styled(Tabs)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    minHeight: '50px',
    '& .MuiTabs-indicator': {
      backgroundColor: isWallpaperMode ? '#FFFFFF' : ACCENT_COLOR,
      height: 3,
      borderRadius: '3px 3px 0 0',
    },
  };
});

const StyledTab = styled(Tab)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    minHeight: '50px',
    textTransform: 'none',
    fontSize: '0.9rem',
    fontWeight: 500,
    color: theme.palette.mode === 'dark'
      ? appleColors.dark.label.primary
      : appleColors.light.label.primary,
    '&.Mui-selected': {
      color: isWallpaperMode ? '#FFFFFF' : ACCENT_COLOR,
      fontWeight: 600,
    }
  };
});

// ACTUALIZADO: ContentArea con altura fija para el contenedor
const ContentArea = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  overflow: 'visible',
  padding: 0,
  position: 'relative',
  minHeight: '300px',
  '@media (max-width: 600px)': {
    padding: 0,
  }
}));

// NUEVO: Contenedor animado que usa transform para 60fps
const AnimatedContentContainer = styled(Box)({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  willChange: 'transform',
  transformOrigin: 'top center',
  transition: 'transform 0.4s cubic-bezier(0.4, 0.0, 0.2, 1)',
});

// ACTUALIZADO: ScrollableContentWrapper sin transform
// ACTUALIZADO: ScrollableContentWrapper con scroll estético
const ScrollableContentWrapper = styled(Box)(({ theme }) => ({
  width: '100%',
  overflowY: 'auto',
  overflowX: 'hidden',
  padding: theme.spacing(3),
  '@media (max-width: 600px)': {
    padding: theme.spacing(2),
  },
  
  // Estilos personalizados para el scrollbar
  '&::-webkit-scrollbar': {
    width: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent',
    borderRadius: '10px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: theme.palette.mode === 'dark' 
      ? 'rgba(255, 255, 255, 0.2)' 
      : 'rgba(0, 0, 0, 0.2)',
    borderRadius: '10px',
    transition: 'background 0.2s ease',
    '&:hover': {
      background: theme.palette.mode === 'dark' 
        ? 'rgba(255, 255, 255, 0.3)' 
        : 'rgba(0, 0, 0, 0.3)',
    },
  },
  
  // Para Firefox
  scrollbarWidth: 'thin',
  scrollbarColor: theme.palette.mode === 'dark'
    ? 'rgba(255, 255, 255, 0.2) transparent'
    : 'rgba(0, 0, 0, 0.2) transparent',
}));

const NavItem = styled(ListItemButton)(({ theme, active }) => ({
  borderRadius: 10,
  marginBottom: theme.spacing(0.5),
  padding: theme.spacing(1, 2),
  backgroundColor: active
    ? theme.palette.actualMode === 'wallpaper'
      ? alpha('#FFFFFF', 0.15)
      : theme.palette.mode === 'dark'
        ? alpha(ACCENT_COLOR, 0.2)
        : alpha(ACCENT_COLOR, 0.1)
    : 'transparent',
  '&:hover': {
    backgroundColor: active
      ? theme.palette.actualMode === 'wallpaper'
        ? alpha('#FFFFFF', 0.2)
        : theme.palette.mode === 'dark'
          ? alpha(ACCENT_COLOR, 0.25)
          : alpha(ACCENT_COLOR, 0.15)
      : theme.palette.actualMode === 'wallpaper'
        ? alpha('#FFFFFF', 0.08)
        : theme.palette.mode === 'dark'
          ? 'rgba(255, 255, 255, 0.05)'
          : 'rgba(0, 0, 0, 0.04)'
  },
  '& .MuiListItemIcon-root': {
    minWidth: 36,
    color: active
      ? theme.palette.actualMode === 'wallpaper'
        ? '#FFFFFF'
        : ACCENT_COLOR
      : undefined
  },
  '& .MuiListItemText-primary': {
    fontWeight: active ? 600 : 500,
    fontSize: '0.95rem',
    color: active
      ? theme.palette.actualMode === 'wallpaper'
        ? '#FFFFFF'
        : ACCENT_COLOR
      : theme.palette.mode === 'dark'
        ? appleColors.dark.label.primary
        : appleColors.light.label.primary
  }
}));

const OptionButton = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'selected',
})(({ theme, selected }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 70,
  height: 70,
  borderRadius: 16,
  backgroundColor: theme.palette.mode === 'dark'
    ? selected ? 'rgba(255, 255, 255, 0.15)' : appleColors.dark.bg.tertiary
    : selected ? appleColors.light.ui.accent : appleColors.light.bg.tertiary,
  color: theme.palette.mode === 'dark'
    ? selected ? '#fff' : 'rgba(255, 255, 255, 0.8)'
    : selected ? '#fff' : theme.palette.text.secondary,
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  position: 'relative',
  border: selected
    ? theme.palette.actualMode === 'wallpaper'
      ? `2px solid #FFFFFF`
      : `2px solid ${ACCENT_COLOR}`
    : `2px solid transparent`,
  '&:hover': {
    backgroundColor: selected
      ? theme.palette.actualMode === 'wallpaper'
        ? 'rgba(255, 255, 255, 0.25)'
        : theme.palette.mode === 'dark'
          ? 'rgba(255, 255, 255, 0.2)'
          : alpha(ACCENT_COLOR, 0.9)
      : theme.palette.actualMode === 'wallpaper'
        ? 'rgba(255, 255, 255, 0.1)'
        : theme.palette.mode === 'dark'
          ? appleColors.dark.system.grey3
          : '#E9E9EB',
    transform: 'translateY(-2px)',
    boxShadow: theme.palette.mode === 'dark'
      ? '0px 3px 8px rgba(0, 0, 0, 0.4)'
      : '0px 4px 12px rgba(0, 0, 0, 0.06)'
  },
  '@media (max-width: 600px)': {
    width: 60,
    height: 60,
    borderRadius: 14,
  }
}));

const CheckmarkBadge = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: -6,
  right: -6,
  width: 20,
  height: 20,
  borderRadius: '50%',
  backgroundColor: theme.palette.actualMode === 'wallpaper'
    ? '#FFFFFF'
    : ACCENT_COLOR,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: theme.palette.mode === 'dark'
    ? '2px solid rgba(0, 0, 0, 0.8)'
    : '2px solid #fff',
  '& svg': {
    color: theme.palette.actualMode === 'wallpaper'
      ? '#000000'
      : '#FFFFFF',
    width: 10,
    height: 10,
  }
}));

const AppleButton = styled(Button)(({ theme, primary }) => ({
  borderRadius: 20,
  padding: theme.spacing(1.2, 3),
  textTransform: 'none',
  fontWeight: 500,
  minWidth: 80,
  backgroundColor: primary
    ? theme.palette.actualMode === 'wallpaper'
      ? '#FFFFFF'
      : ACCENT_COLOR
    : theme.palette.mode === 'dark'
      ? appleColors.dark.bg.tertiary
      : appleColors.light.bg.tertiary,
  color: primary
    ? theme.palette.actualMode === 'wallpaper'
      ? '#000000'
      : '#fff'
    : theme.palette.actualMode === 'wallpaper'
      ? '#FFFFFF'
      : theme.palette.mode === 'dark'
        ? '#fff'
        : ACCENT_COLOR,
  '&:hover': {
    backgroundColor: primary
      ? theme.palette.actualMode === 'wallpaper'
        ? 'rgba(255, 255, 255, 0.85)'
        : alpha(ACCENT_COLOR, 0.9)
      : theme.palette.actualMode === 'wallpaper'
        ? alpha('#FFFFFF', 0.1)
        : theme.palette.mode === 'dark'
          ? 'rgba(255, 255, 255, 0.15)'
          : alpha(appleColors.light.bg.tertiary, 0.9),
    transform: 'translateY(-1px)',
  },
  '@media (max-width: 600px)': {
    padding: theme.spacing(1, 2),
    minWidth: 70,
  }
}));

const OptionName = styled(Typography)(({ theme, selected }) => ({
  fontSize: 14,
  marginTop: 8,
  fontWeight: selected ? 600 : 500,
  color: theme.palette.mode === 'dark'
    ? appleColors.dark.label.primary
    : selected ? ACCENT_COLOR : appleColors.light.label.primary,
  '@media (max-width: 600px)': {
    fontSize: 13,
  }
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  marginBottom: theme.spacing(3),
  fontSize: '1.1rem',
  color: theme.palette.actualMode === 'wallpaper'
    ? '#FFFFFF'
    : theme.palette.mode === 'dark'
      ? appleColors.dark.label.primary
      : appleColors.light.label.primary,
  position: 'relative',
  paddingBottom: theme.spacing(1),
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: 0,
    width: 40,
    height: 3,
    borderRadius: 1.5,
    backgroundColor: theme.palette.actualMode === 'wallpaper'
      ? '#FFFFFF'
      : ACCENT_COLOR,
  }
}));

const DialogFooter = styled(Box)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    padding: theme.spacing(2, 3),
    borderTop: `1px solid ${isWallpaperMode
      ? 'rgba(55, 55, 55, 0.1)'
      : (theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.1)'
        : 'rgba(0, 0, 0, 0.06)'
      )}`,
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(2)
  };
});

const AppleSelect = styled(Select)(({ theme }) => ({
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.mode === 'dark'
      ? 'rgba(255, 255, 255, 0.23)'
      : 'rgba(0, 0, 0, 0.23)',
    borderRadius: 12,
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.actualMode === 'wallpaper'
      ? '#FFFFFF'
      : ACCENT_COLOR,
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.actualMode === 'wallpaper'
      ? '#FFFFFF'
      : ACCENT_COLOR,
    borderWidth: 2,
  },
  '& .MuiSelect-select': {
    padding: '12px 16px',
    borderRadius: 12,
    backgroundColor: theme.palette.mode === 'dark'
      ? appleColors.dark.bg.tertiary
      : appleColors.light.bg.tertiary,
  },
  borderRadius: 12,
  transition: 'all 0.2s',
}));

const AppleMenuItem = styled(MenuItem)(({ theme }) => ({
  padding: '10px 16px',
  margin: '2px',
  borderRadius: 8,
  '&:hover': {
    backgroundColor: theme.palette.actualMode === 'wallpaper'
      ? alpha('#FFFFFF', 0.1)
      : theme.palette.mode === 'dark'
        ? alpha(ACCENT_COLOR, 0.15)
        : alpha(ACCENT_COLOR, 0.1),
  },
  '&.Mui-selected': {
    backgroundColor: theme.palette.actualMode === 'wallpaper'
      ? alpha('#FFFFFF', 0.15)
      : theme.palette.mode === 'dark'
        ? alpha(ACCENT_COLOR, 0.2)
        : alpha(ACCENT_COLOR, 0.15),
    '&:hover': {
      backgroundColor: theme.palette.actualMode === 'wallpaper'
        ? alpha('#FFFFFF', 0.2)
        : theme.palette.mode === 'dark'
          ? alpha(ACCENT_COLOR, 0.25)
          : alpha(ACCENT_COLOR, 0.2),
    }
  },
}));

const AppleFormControl = styled(FormControl)(({ theme }) => ({
  marginTop: theme.spacing(2),
  '& .MuiInputLabel-root': {
    color: theme.palette.mode === 'dark'
      ? appleColors.dark.system.grey
      : appleColors.light.system.grey,
    '&.Mui-focused': {
      color: ACCENT_COLOR,
    }
  },
  '& .MuiSelect-icon': {
    color: theme.palette.mode === 'dark'
      ? appleColors.dark.system.grey
      : appleColors.light.system.grey,
  }
}));

const LanguageDescription = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(2),
  color: theme.palette.mode === 'dark'
    ? appleColors.dark.system.grey
    : appleColors.light.system.grey,
  fontSize: '0.875rem',
  lineHeight: 1.5,
}));

// Componentes móvil
const MobileBackdrop = styled(Box)(({ theme, open }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'transparent',
  zIndex: theme.zIndex.modal - 1,
  opacity: open ? 1 : 0,
  transition: 'opacity 250ms ease',
  visibility: open ? 'visible' : 'hidden',
}));

const MobileModalContainer = styled(motion.div)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    position: 'fixed',
    bottom: 0,
    left: 0,
    width: '100%',
    borderTopLeftRadius: '16px',
    borderTopRightRadius: '16px',
    backgroundColor: isWallpaperMode
      ? 'rgba(128, 128, 128, 0.3)'
      : (theme.palette.mode === 'dark'
        ? '#1C1C1E'
        : appleColors.light.bg.primaryElevated),
    backdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    WebkitBackdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    border: isWallpaperMode ? '1px solid rgba(128, 128, 128, 0.3)' : 'none',
    boxShadow: theme.palette.mode === 'dark'
      ? '0 -2px 20px rgba(0, 0, 0, 0.5)'
      : '0 -2px 20px rgba(0, 0, 0, 0.15)',
    zIndex: theme.zIndex.modal,
    maxHeight: '85vh',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    touchAction: 'auto',
  };
});

const DragArea = styled(Box)({
  width: '100%',
  touchAction: 'none',
  cursor: 'grab',
  '&:active': {
    cursor: 'grabbing',
  }
});

const DragHandle = styled(Box)(({ theme }) => ({
  width: '36px',
  height: '5px',
  borderRadius: '3px',
  backgroundColor: theme.palette.mode === 'dark'
    ? appleColors.dark.system.grey
    : appleColors.light.system.grey,
  opacity: 0.3,
  margin: '12px auto 4px',
  cursor: 'grab',
  '&:active': {
    cursor: 'grabbing',
    opacity: 0.5,
  }
}));

const MobileHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  position: 'relative',
  padding: '0 16px 10px'
}));

const MobileContent = styled(Box)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    flexGrow: 1,
    overflowY: 'auto',
    padding: theme.spacing(2),
    paddingBottom: theme.spacing(4),
    backgroundColor: isWallpaperMode
      ? 'transparent'
      : (theme.palette.mode === 'dark'
        ? '#1C1C1E'
        : appleColors.light.bg.primaryElevated),
    backdropFilter: 'none',
    WebkitBackdropFilter: 'none',
  };
});

const MobileFooter = styled(Box)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    position: 'sticky',
    bottom: 0,
    width: '100%',
    backgroundColor: isWallpaperMode
      ? 'transparent'
      : (theme.palette.mode === 'dark'
        ? '#1C1C1E'
        : appleColors.light.bg.primaryElevated),
    backdropFilter: 'none',
    WebkitBackdropFilter: 'none',
    borderTop: `1px solid ${isWallpaperMode
      ? 'rgba(128, 128, 128, 0.3)'
      : (theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.1)'
        : 'rgba(0, 0, 0, 0.06)'
      )}`,
    padding: theme.spacing(2, 3, 4),
    display: 'flex',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    zIndex: 10,
    touchAction: 'auto',
  };
});

const MobileButton = styled(Button)(({ theme, color = 'primary', fullWidth = false }) => {
  const isCancel = color === 'inherit';
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    borderRadius: '12px',
    padding: '10px 16px',
    textTransform: 'none',
    fontWeight: 500,
    fontSize: '16px',
    flex: fullWidth ? 1 : 'auto',
    boxShadow: 'none',
    ...(isCancel ? {
      color: isWallpaperMode ? '#FFFFFF' : (theme.palette.mode === 'dark' ? '#FF3B30' : '#FF3B30'),
      '&:hover': {
        backgroundColor: 'transparent',
      }
    } : {
      backgroundColor: isWallpaperMode ? '#FFFFFF' : ACCENT_COLOR,
      color: isWallpaperMode ? '#000000' : '#ffffff',
      '&:hover': {
        backgroundColor: isWallpaperMode
          ? 'rgba(255, 255, 255, 0.85)'
          : (theme.palette.mode === 'dark' ? '#0371DB' : '#0069D9'),
        boxShadow: 'none',
      }
    })
  };
});

const Settings = ({
  open,
  onClose,
  onBackgroundChange,
  currentBackground,
  onStartupTabChange,
  currentStartupTab,
  onLanguageChange,
  currentLanguage = 'auto',
  onWallpaperChange,
  currentWallpaper = 'appartment',
  selectedApps = ['home', 'chat'],
  user
}) => {
  // Separar tema y wallpaper del currentBackground
  const [themeMode, setThemeMode] = useState(() => {
    // Decodificar el backgroundType
    if (currentBackground === 'wallpaper') return 'system';
    if (currentBackground === 'wallpaper-light') return 'light';
    if (currentBackground === 'wallpaper-dark') return 'dark';
    return currentBackground; // 'system', 'light', 'dark'
  });
  const [wallpaperType, setWallpaperType] = useState(() => {
    // Si currentBackground incluye 'wallpaper', usar currentWallpaper, sino 'none'
    return currentBackground?.startsWith('wallpaper') ? currentWallpaper : 'none';
  });

  const [startupTab, setStartupTab] = useState(currentStartupTab || 'default');
  const [language, setLanguage] = useState(currentLanguage || 'auto');
  const [activeSection, setActiveSection] = useState('appearance');
  const [localSelectedApps, setLocalSelectedApps] = useState(selectedApps);
  const [isSaving, setIsSaving] = useState(false);
  const [contentHeight, setContentHeight] = useState(300);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useTranslation();
  const contentRef = useRef(null);

  // Generate startup tab options based on selected apps
  const startupTabOptions = getStartupTabOptions(localSelectedApps);

  // Update state when props change
  useEffect(() => {
    if (currentStartupTab) {
      setStartupTab(currentStartupTab);
    }
    if (currentLanguage) {
      setLanguage(currentLanguage);
    }
    if (selectedApps) {
      setLocalSelectedApps(selectedApps);
    }
    // Sincronizar wallpaperType con currentWallpaper cuando cambia
    if (currentBackground?.startsWith('wallpaper') && currentWallpaper) {
      setWallpaperType(currentWallpaper);
    } else if (!currentBackground?.startsWith('wallpaper')) {
      setWallpaperType('none');
    }
  }, [currentStartupTab, currentLanguage, selectedApps, currentWallpaper, currentBackground]);

  // ACTUALIZADO: Medir altura real del contenido
  useEffect(() => {
    const measureHeight = () => {
      if (contentRef.current) {
        // Obtener la altura real del contenido
        const height = contentRef.current.scrollHeight;
        // Asegurar un mínimo de 300px
        setContentHeight(Math.max(300, height));
      }
    };

    // Medir inmediatamente
    measureHeight();

    // Medir después de un pequeño delay para elementos que se cargan tarde
    const timeoutId = setTimeout(measureHeight, 50);

    // Observer para cambios de tamaño
    const resizeObserver = new ResizeObserver(() => {
      requestAnimationFrame(measureHeight);
    });

    if (contentRef.current) {
      resizeObserver.observe(contentRef.current);
    }

    return () => {
      clearTimeout(timeoutId);
      resizeObserver.disconnect();
    };
  }, [activeSection, localSelectedApps]);

  // Obtener aplicaciones disponibles para añadir (no seleccionadas actualmente)
  const getAvailableAppsToAdd = () => {
    return availableApps.filter(app =>
      !localSelectedApps.includes(app.id) && !app.required
    );
  };

  // Manejar añadir aplicación
  const handleAddApp = (appId) => {
    if (!localSelectedApps.includes(appId)) {
      setLocalSelectedApps(prev => [...prev, appId]);
    }
  };

  // Manejar remover aplicación
  const handleRemoveApp = (appId) => {
    const app = availableApps.find(a => a.id === appId);
    if (app && !app.required) {
      setLocalSelectedApps(prev => prev.filter(id => id !== appId));

      // Si la app removida era el tab de inicio, cambiar a 'default'
      if (startupTab === appId) {
        setStartupTab('default');
      }
    }
  };

  // Get current language info
  const getCurrentLanguageInfo = () => {
    const lang = languages.find(l => l.code === language);
    return lang || { name: 'Unknown', nativeName: '' };
  };

  const currentLangInfo = getCurrentLanguageInfo();

  // Get current startup tab info
  const getCurrentStartupTabInfo = () => {
    const option = startupTabOptions.find(option => option.type === startupTab);
    return option || { name: 'Default', description: 'Use system behavior' };
  };

  const currentStartupTabInfo = getCurrentStartupTabInfo();

  // Función helper para combinar tema y wallpaper
  const combineBackgroundSettings = (theme, wallpaper) => {
    // Check if wallpaper is enabled (not 'none')
    if (wallpaper !== 'none') {
      // Wallpaper activado: codificar el tema
      if (theme === 'system') return 'wallpaper';
      if (theme === 'light') return 'wallpaper-light';
      if (theme === 'dark') return 'wallpaper-dark';
    }
    // Sin wallpaper: retornar el tema directamente
    return theme;
  };

  // Handlers
  const handleThemeModeChange = (newThemeMode) => {
    setThemeMode(newThemeMode);
    const combinedBackground = combineBackgroundSettings(newThemeMode, wallpaperType);
    onBackgroundChange(combinedBackground);
  };

  const handleWallpaperChange = async (newWallpaperType) => {
    setWallpaperType(newWallpaperType);
    const combinedBackground = combineBackgroundSettings(themeMode, newWallpaperType);
    onBackgroundChange(combinedBackground);

    // Notificar al componente padre del cambio de wallpaper
    if (onWallpaperChange && newWallpaperType !== 'none') {
      onWallpaperChange(newWallpaperType);
    }

    // Save wallpaper preference to backend
    if (user?.uid && newWallpaperType !== 'none') {
      try {
        await setUserWallpaperPreference(user.uid, newWallpaperType);
      } catch (error) {
        console.error('Error saving wallpaper preference:', error);
      }
    }
  };

  const handleStartupTabChange = (tabType) => {
    setStartupTab(tabType);
  };

  const handleLanguageChange = (event) => {
    const langCode = event.target.value;
    setLanguage(langCode);
  };

  const handleSave = async () => {
    setIsSaving(true);

    try {
      // Guardar cambios en las aplicaciones seleccionadas si hay cambios
      if (JSON.stringify(localSelectedApps.sort()) !== JSON.stringify(selectedApps.sort())) {
        if (user?.uid) {
          // Mantener el defaultApp actual o usar 'home' si no está en las apps seleccionadas
          let newDefaultApp = startupTab === 'default' ? 'home' : startupTab;
          if (!localSelectedApps.includes(newDefaultApp)) {
            newDefaultApp = 'home';
          }

          await saveUserSelectedApps(user.uid, localSelectedApps, newDefaultApp);

          // Actualizar los flags de navegación en localStorage y disparar evento personalizado
          const showUpsInNav = localSelectedApps.includes('ups');
          const showEduInNav = localSelectedApps.includes('edu');
          const showFinInNav = localSelectedApps.includes('fin');
          const showHealthInNav = localSelectedApps.includes('health');
          const showFitInNav = localSelectedApps.includes('fit');

          localStorage.setItem('showUpsInNav', showUpsInNav.toString());
          localStorage.setItem('showEduInNav', showEduInNav.toString());
          localStorage.setItem('showFinInNav', showFinInNav.toString());
          localStorage.setItem('showHealthInNav', showHealthInNav.toString());
          localStorage.setItem('showFitInNav', showFitInNav.toString());
          localStorage.setItem('selectedApps', JSON.stringify(localSelectedApps));

          // Disparar evento personalizado para que FullDashboard actualice la navegación
          window.dispatchEvent(new CustomEvent('appsConfigurationChanged', {
            detail: {
              selectedApps: localSelectedApps,
              showUpsInNav,
              showEduInNav,
              showFinInNav,
              showHealthInNav,
              showFitInNav
            }
          }));
        }
      }

      // Guardar otros cambios
      if (startupTab !== currentStartupTab && onStartupTabChange) {
        onStartupTabChange(startupTab);
      }

      if (language !== currentLanguage && onLanguageChange) {
        onLanguageChange(language);
      }

      onClose();
    } catch (error) {
      console.error('Error saving settings:', error);
    } finally {
      setIsSaving(false);
    }
  };

  // Tab navigation
  const getTabIndex = () => {
    const index = navItems.findIndex(item => item.id === activeSection);
    // Si no se encuentra, retornar 0 (primera pestaña) como fallback
    return index !== -1 ? index : 0;
  };

  const handleTabChange = (event, newValue) => {
    if (newValue >= 0 && newValue < navItems.length) {
      setActiveSection(navItems[newValue].id);
    }
  };

  // Framer Motion drag end handler for mobile
  const handleDragEnd = (_, info) => {
    if (info.offset.y > window.innerHeight * 0.25) {
      onClose();
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Section content rendering
  const renderContent = () => {
    const content = {
      appearance: (
        <Box>
          {!isMobile && <SectionTitle>{t('settings.appearance')}</SectionTitle>}

          {/* Theme Mode Section */}
          <Typography variant="subtitle2" sx={{
            mb: 2,
            mt: isMobile ? 1 : 0,
            fontWeight: 600,
            fontSize: '0.9rem',
            color: theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000',
            opacity: 0.7
          }}>
            Theme Mode
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, justifyContent: 'flex-start', mb: 4 }}>
            {appearanceOptions.map((option, index) => {
              const Icon = option.icon;
              const isSelected = themeMode === option.type;

              return (
                <Grow key={option.type} in={true} timeout={(index + 1) * 200}>
                  <Box
                    sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}
                    onClick={() => handleThemeModeChange(option.type)}
                  >
                    <OptionButton selected={isSelected}>
                      <Icon size={isMobile ? 24 : 28} />
                      {isSelected && (
                        <CheckmarkBadge>
                          <Check />
                        </CheckmarkBadge>
                      )}
                    </OptionButton>
                    <OptionName selected={isSelected}>
                      {t(`appearance.${option.type}`)}
                    </OptionName>
                  </Box>
                </Grow>
              );
            })}
          </Box>

          {/* Wallpaper Section */}
          <Typography variant="subtitle2" sx={{
            mb: 2,
            fontWeight: 600,
            fontSize: '0.9rem',
            color: theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000',
            opacity: 0.7
          }}>
            Wallpaper
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, justifyContent: 'flex-start' }}>
            {wallpaperOptions.map((option, index) => {
              const Icon = option.icon;
              const isSelected = wallpaperType === option.type;

              return (
                <Grow key={option.type} in={true} timeout={(index + 4) * 200}>
                  <Box
                    sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}
                    onClick={() => handleWallpaperChange(option.type)}
                  >
                    <OptionButton selected={isSelected}>
                      {option.preview ? (
                        <Box
                          sx={{
                            width: '100%',
                            height: '100%',
                            backgroundImage: `url(${option.preview})`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            borderRadius: 'inherit'
                          }}
                        />
                      ) : (
                        <Icon size={isMobile ? 24 : 28} />
                      )}
                      {isSelected && (
                        <CheckmarkBadge>
                          <Check />
                        </CheckmarkBadge>
                      )}
                    </OptionButton>
                    <OptionName selected={isSelected}>
                      {option.name}
                    </OptionName>
                  </Box>
                </Grow>
              );
            })}
          </Box>
        </Box>
      ),
      startupTab: (
        <Box>
          {!isMobile && <SectionTitle>Ecosystem Management</SectionTitle>}

          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{
              mb: 2,
              fontSize: '1rem',
              fontWeight: 600,
              color: (theme) => theme.palette.actualMode === 'wallpaper'
                ? '#FFFFFF'
                : theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000'
            }}>
              Current Apps
            </Typography>

            <Grid container spacing={2}>
              {localSelectedApps.map(appId => {
                const app = availableApps.find(a => a.id === appId);
                if (!app) return null;

                const isRemovable = !app.required;

                return (
                  <Grid item xs={6} sm={4} md={3} key={app.id}>
                    <AppCard selected={true} removable={isRemovable}>
                      <AppIcon gradient={app.gradient} gradientDark={app.gradientDark} appId={app.id}>
                        {app.gradient ? (
                          <BrainIconWithGradient
                            gradient={app.gradient}
                            gradientDark={app.gradientDark}
                            appId={app.id}
                          />
                        ) : app.icon ? (
                          <app.icon size={24} />
                        ) : null}
                      </AppIcon>
                      <AppTextContainer>
                        <AppName>{app.name}</AppName>
                        <AppDescription>{app.description}</AppDescription>
                      </AppTextContainer>

                      {isRemovable && (
                        <RemoveButton onClick={() => handleRemoveApp(app.id)}>
                          <X />
                        </RemoveButton>
                      )}
                    </AppCard>
                  </Grid>
                );
              })}
            </Grid>
          </Box>

          <Divider sx={{ my: 3 }} />

          {getAvailableAppsToAdd().length > 0 && (
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{
                mb: 2,
                fontSize: '1rem',
                fontWeight: 600,
                color: (theme) => theme.palette.actualMode === 'wallpaper'
                  ? '#FFFFFF'
                  : theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000'
              }}>
                Available Apps
              </Typography>

              <Grid container spacing={2}>
                {getAvailableAppsToAdd().map(app => {
                  return (
                    <Grid item xs={6} sm={4} md={3} key={app.id}>
                      <AppCard
                        selected={false}
                        onClick={() => handleAddApp(app.id)}
                      >
                        <AppIcon gradient={app.gradient} gradientDark={app.gradientDark} appId={app.id}>
                          {app.gradient ? (
                            <BrainIconWithGradient
                              gradient={app.gradient}
                              gradientDark={app.gradientDark}
                              appId={app.id}
                            />
                          ) : app.icon ? (
                            <app.icon size={24} />
                          ) : null}
                        </AppIcon>
                        <AppTextContainer>
                          <AppName>{app.name}</AppName>
                          <AppDescription>{app.description}</AppDescription>
                        </AppTextContainer>

                        <AddButton onClick={(e) => {
                          e.stopPropagation();
                          handleAddApp(app.id);
                        }}>
                          <Plus />
                        </AddButton>
                      </AppCard>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          )}

          <Divider sx={{ my: 3 }} />

          <Box>
            <Typography variant="h6" sx={{
              mb: 2,
              fontSize: '1rem',
              fontWeight: 600,
              color: (theme) => theme.palette.actualMode === 'wallpaper'
                ? '#FFFFFF'
                : theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000'
            }}>
              Default App on Startup
            </Typography>

            <AppleFormControl fullWidth>
              <InputLabel id="startup-tab-select-label">Startup App</InputLabel>
              <AppleSelect
                labelId="startup-tab-select-label"
                id="startup-tab-select"
                value={startupTab}
                onChange={(e) => handleStartupTabChange(e.target.value)}
                label="Startup App"
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 300,
                      borderRadius: 16,
                      padding: '8px'
                    },
                  },
                }}
              >
                {startupTabOptions.map((option) => {
                  return (
                    <AppleMenuItem key={option.type} value={option.type}>
                      <Box>
                        <Typography variant="body1">
                          {option.type === 'default' ? 'Default' : option.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.description}
                        </Typography>
                      </Box>
                    </AppleMenuItem>
                  );
                })}
              </AppleSelect>
            </AppleFormControl>

            <LanguageDescription>
              {startupTab === 'default'
                ? 'Use system behavior to determine startup screen'
                : `Always start with ${currentStartupTabInfo.name}`
              }
            </LanguageDescription>
          </Box>
        </Box>
      ),
      language: (
        <Box>
          {!isMobile && <SectionTitle>{t('settings.language')}</SectionTitle>}
          <Box sx={{ mt: isMobile ? 1 : 0 }}>
            <AppleFormControl fullWidth>
              <InputLabel id="language-select-label">{t('language.selectLabel')}</InputLabel>
              <AppleSelect
                labelId="language-select-label"
                id="language-select"
                value={language}
                onChange={handleLanguageChange}
                label={t('language.selectLabel')}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 300,
                      borderRadius: 16,
                      padding: '8px'
                    },
                  },
                }}
                renderValue={(selected) => {
                  const lang = languages.find(l => l.code === selected);
                  return lang ? `${lang.name} ${lang.nativeName !== lang.name ? `(${lang.nativeName})` : ''}` : selected;
                }}
              >
                {languages.map((lang) => (
                  <AppleMenuItem key={lang.code} value={lang.code}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {!isMobile && (
                        lang.code === 'auto' ? (
                          <Globe size={18} style={{ marginRight: 8 }} />
                        ) : (
                          <Box
                            sx={{
                              minWidth: '24px',
                              marginRight: '8px',
                              fontWeight: 'bold',
                              fontSize: '0.8rem'
                            }}
                          >
                            {lang.code.toUpperCase().substring(0, 2)}
                          </Box>
                        )
                      )}
                      <Box>
                        <Typography variant="body1">{lang.name}</Typography>
                        {lang.nativeName !== lang.name && (
                          <Typography variant="caption" color="text.secondary">
                            {lang.nativeName}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </AppleMenuItem>
                ))}
              </AppleSelect>
            </AppleFormControl>

            <LanguageDescription>
              {language === 'auto'
                ? t('language.autoDetectDescription')
                : t('language.selectedDescription', {
                  name: `${currentLangInfo.name}${currentLangInfo.nativeName !== currentLangInfo.name ? ` (${currentLangInfo.nativeName})` : ''}`
                })
              }
            </LanguageDescription>
          </Box>
        </Box>
      )
    };

    return (
      <AnimatePresence mode="wait">
        <motion.div
          key={activeSection}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          transition={{
            duration: 0.3,
            ease: [0.16, 1, 0.3, 1]
          }}
          style={{ width: '100%' }}
        >
          {content[activeSection]}
        </motion.div>
      </AnimatePresence>
    );
  };

  // Renderizado condicional basado en si es móvil o escritorio
  if (isMobile) {
    return (
      <>
        <MobileBackdrop open={open} onClick={handleBackdropClick} />

        <Modal
          open={open}
          onClose={onClose}
          closeAfterTransition
          disableAutoFocus={true}
          disableEnforceFocus={true}
          disableRestoreFocus={true}
          disableScrollLock={false}
          keepMounted={true}
          BackdropProps={{
            invisible: true,
          }}
        >
          <Box>
            <AnimatePresence>
              {open && (
                <MobileModalContainer
                initial={{ y: '100%' }}
                animate={{ y: 0 }}
                exit={{ y: '100%' }}
                transition={{
                  type: 'spring',
                  damping: 30,
                  stiffness: 300,
                  mass: 0.8
                }}
                drag="y"
                dragConstraints={{ top: 0, bottom: window.innerHeight }}
                dragElastic={0.6}
                onDragEnd={handleDragEnd}
                dragDirectionLock
                dragMomentum={true}
                dragTransition={{
                  min: 0,
                  max: 200,
                  power: 0.2
                }}
              >
                <DragArea>
                  <DragHandle />

                  <MobileHeader>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 700,
                        fontSize: '24px',
                        color: theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000'
                      }}
                    >
                      {t('settings.title')}
                    </Typography>
                    <IconButton
                      onClick={onClose}
                      sx={{
                        position: 'absolute',
                        right: 8,
                        color: theme.palette.mode === 'dark' ? '#8E8E93' : '#8E8E93'
                      }}
                    >
                      <ChevronDown size={20} />
                    </IconButton>
                  </MobileHeader>
                </DragArea>

                <MobileNavigation>
                  <StyledTabs
                    value={getTabIndex()}
                    onChange={handleTabChange}
                    variant="fullWidth"
                    aria-label="settings navigation"
                  >
                    {navItems.map((item) => (
                      <StyledTab
                        key={item.id}
                        label={item.id === 'startupTab' ? 'Ecosystem' : t(`settings.${item.id}`)}
                      />
                    ))}
                  </StyledTabs>
                </MobileNavigation>

                <MobileContent>
                  {renderContent()}
                </MobileContent>

                <MobileFooter>
                  <MobileButton
                    onClick={onClose}
                    color="inherit"
                    fullWidth
                  >
                    {t('settings.cancel')}
                  </MobileButton>
                  <MobileButton
                    onClick={handleSave}
                    variant="contained"
                    fullWidth
                    disabled={isSaving}
                  >
                    {isSaving ? 'Saving...' : t('settings.save')}
                  </MobileButton>
                </MobileFooter>
              </MobileModalContainer>
            )}
            </AnimatePresence>
          </Box>
        </Modal>
      </>
    );
  } else {
    // VERSIÓN DESKTOP OPTIMIZADA
    return (
      <>
        <BlurBackdrop open={open} />
        <AppleDialog
          open={open}
          onClose={onClose}
          maxWidth="md"
          fullWidth
        >
          <DialogHeader>
            <Typography
              variant="h6"
              component="div"
              fontWeight={600}
              sx={{
                color: theme => theme.palette.mode === 'dark'
                  ? appleColors.dark.label.primary
                  : appleColors.light.label.primary
              }}
            >
              {t('settings.title')}
            </Typography>
            <IconButton
              onClick={onClose}
              size="small"
              sx={{
                color: theme => theme.palette.mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.8)'
                  : appleColors.light.system.grey,
                '&:hover': {
                  backgroundColor: theme => theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.1)'
                    : alpha(appleColors.light.bg.tertiary, 0.8)
                }
              }}
            >
              <X size={20} />
            </IconButton>
          </DialogHeader>

          <ContentContainer>
            <SideNavigation>
              <List sx={{ p: 0, width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                {navItems.map(item => (
                  <NavItem
                    key={item.id}
                    active={activeSection === item.id}
                    onClick={() => setActiveSection(item.id)}
                  >
                    <ListItemIcon>
                      <item.icon size={20} />
                    </ListItemIcon>
                    <ListItemText primary={item.id === 'startupTab' ? 'Ecosystem' : t(`settings.${item.id}`)} />
                  </NavItem>
                ))}
              </List>
            </SideNavigation>

            <MobileNavigation>
              <StyledTabs
                value={getTabIndex()}
                onChange={handleTabChange}
                variant="fullWidth"
                aria-label="settings navigation"
              >
                {navItems.map((item) => (
                  <StyledTab
                    key={item.id}
                    label={item.id === 'startupTab' ? 'Ecosystem' : t(`settings.${item.id}`)}
                  />
                ))}
              </StyledTabs>
            </MobileNavigation>

            {/* ACTUALIZADO: ContentArea con animación GPU-acelerada */}
            <ContentArea
              sx={{
                height: `${contentHeight}px`,
                transition: 'height 0.4s cubic-bezier(0.4, 0.0, 0.2, 1)',
              }}
            >
              <AnimatedContentContainer
                sx={{
                  height: `${contentHeight}px`,
                }}
              >
                <ScrollableContentWrapper
                  sx={{
                    height: `${contentHeight}px`,
                  }}
                >
                  <Box ref={contentRef}>
                    {renderContent()}
                  </Box>
                </ScrollableContentWrapper>
              </AnimatedContentContainer>
            </ContentArea>
          </ContentContainer>

          <DialogFooter>
            <AppleButton onClick={onClose}>{t('settings.cancel')}</AppleButton>
            <AppleButton onClick={handleSave} primary disabled={isSaving}>
              {isSaving ? 'Saving...' : t('settings.save')}
            </AppleButton>
          </DialogFooter>
        </AppleDialog>
      </>
    );
  }
};

export default Settings;